import { onMounted, onUnmounted, ref } from "vue"

/**
 * useExitModal composable with optional automatic mounting.
 *
 * @param autoMount - Determines whether to automatically add/remove the exit modal listener on mount/unmount.
 *
 * @example // Automatic mounting and unmounting.
 * useExitModal()
 *
 * @example // Manual mounting and unmounting.
 * const { addExitModal, removeExitModal, isModalActive } = useExitModal(false)
 *
 * // Adds the exit modal listener.
 * function someFunction() {
 *   addExitModal()
 * }
 *
 * // Removes the exit modal listener.
 * function someOtherFunction() {
 * removeExitModal()
 * }
 */
export function useExitModal(autoMount: boolean = true) {
  const isModalActive = ref(false)

  /**
   * Handler for the beforeunload event.
   * Prompts the user with a confirmation dialog when attempting to leave the page.
   */
  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (isModalActive.value) {
      event.preventDefault() // Standard method to cancel the event.
      event.returnValue = "" // Required for Chrome to display the confirmation dialog.
    }
  }

  /**
   * Adds the beforeunload event listener.
   */
  const addExitModal = () => {
    if (!isModalActive.value) {
      isModalActive.value = true
      window.addEventListener("beforeunload", handleBeforeUnload)
    }
  }

  /**
   * Removes the beforeunload event listener.
   */
  const removeExitModal = () => {
    if (isModalActive.value) {
      isModalActive.value = false
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }

  /**
   * Automatically adds the beforeunload event listener if autoMount is true.
   */
  if (autoMount) {
    onMounted(() => {
      addExitModal()
    })

    onUnmounted(() => {
      removeExitModal()
    })
  }

  /**
   * Automatically removes the beforeunload event listener if autoMount is true.
   */

  return {
    addExitModal,
    removeExitModal,
    isModalActive
  }
}
